import { KpiDelta } from '../kpi-delta';
import type { ContractFinance, EacMode, MarginMode } from '@forecastify/shared';
import { eacFormula, eacHint, eacLabel, eacMenuItems } from '../eac-mode';
import { DropdownMenu, Icon } from '@forecastify/ui';
import { useT } from '../../../i18n';
import { FinanceTip, type FinanceTipText } from './finance-tip';
import { barWidth, index, money, percent, signedMoney, type Currency } from './finance-format';
import type { FinanceModel } from './finance-model';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C58 · the eight tiles, the budget bars and the profitability list.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TILE SET DEPENDS ON THE CONTRACT TYPE AND THE TWO SETS ARE NOT COSMETIC VARIANTS.**
 * A recurring contract's reader is asking *"is this month going to fit"* — run rate, projected
 * overrun, the period's margin. A fixed-price one's is asking *"is this job going to fit"* — SPI,
 * EAC, the margin at close. Showing one set for both would put a run rate on a project with no
 * periods, and a schedule index on a retainer that has no schedule to be behind.
 */

const DESTRUCTIVE = 'text-destructive';

function bad(condition: boolean): string {
  return condition ? DESTRUCTIVE : '';
}

/* ── the tiles ─────────────────────────────────────────────────────────────── */

interface Tile {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  /** 3 Nov 2026 · the raw number behind `value`, which `KpiDelta` compares — never the text. */
  readonly figure: number | null;
  readonly valueClass?: string;
  readonly formula?: string;
  readonly sub?: string;
  readonly tip: FinanceTipText;
  /** A label that is itself a control — *melyik margó*, *melyik EAC*. */
  readonly menu?: readonly {
    label: string;
    description?: string;
    checked: boolean;
    /* 30 Oct 2026 · EV · a disabled EAC is listed, greyed. */
    disabled?: boolean;
    /** 2 Nov 2026 · why it is greyed, in the kit Tooltip — the description says what it IS. */
    tooltip?: string;
    onSelect: () => void;
  }[];
  readonly bar?: { readonly pct: number; readonly color: string };
  /** ⚠️ The second, fainter bar is ELAPSED TIME, so the two lengths can be compared at a glance. */
  readonly timeBar?: number;
}

/** The tiles made of income: hidden on an internal project. */
const INCOME_TILES: ReadonlySet<string> = new Set(['revenue', 'margin', 'periodMargin', 'layers']);

/**
 * 30 Oct 2026 · the tiles that measure against a BUDGET, which a time-and-material line item does
 * not have — its frame is its rate × the hours, so *budget*, *period spend / budget* and *forecast
 * overrun* would all be measured against nothing (4D Soft-ONE on UAT).
 */
const BUDGET_TILES: ReadonlySet<string> = new Set(['budget', 'periodSpend', 'overrun']);

export function FinanceKpis({
  view,
  model,
  currency,
  eacMode,
  marginMode,
  onEacMode,
  onMarginMode,
  internal = false,
  noBudget = false,
  live = null,
}: {
  readonly view: ContractFinance;
  readonly model: FinanceModel;
  readonly currency: Currency;
  readonly eacMode: EacMode;
  readonly marginMode: MarginMode;
  readonly onEacMode: (mode: EacMode) => void;
  readonly onMarginMode: (mode: MarginMode) => void;
  /** 30 Oct 2026 · today's figures, under a past day's — *Eltérés a jelenhez*. */
  readonly live?: { readonly view: ContractFinance; readonly model: FinanceModel } | null;
  /**
   * 21 Oct 2026 · **an internal project earns nothing** — it has a budget and spending and no
   * client to bill, so every figure made of income (revenue, margin, profitability) is hidden on it
   * rather than drawn as a zero that reads like a loss.
   */
  readonly internal?: boolean;
  /** 30 Oct 2026 · every line item in view is time-and-material — `BUDGET_TILES` go. */
  readonly noBudget?: boolean;
}) {
  const t = useT();
  /* 30 Oct 2026 · the tiles, for whichever figures — the shown day's, and today's beside them. */
  const tilesOf = (view: ContractFinance, model: FinanceModel): Tile[] => {
    const m = (value: number | null): string => money(value, currency);
    const s = (value: number | null): string => signedMoney(value, currency);

    const marginMenu = [
      {
        label: t('finance.margin.plan'),
        description: t('finance.margin.planHint'),
        checked: marginMode === 'plan',
        onSelect: () => {
          onMarginMode('plan');
        },
      },
      {
        label: t('finance.margin.forecast'),
        description: t('finance.margin.forecastHint'),
        checked: marginMode === 'forecast',
        onSelect: () => {
          onMarginMode('forecast');
        },
      },
    ];

    const eacMenu = eacMenuItems(t, view.eac, eacMode, onEacMode);

    const period = model.currentPeriod;
    const periodShare =
      period === null || Number(period.budget) <= 0.4
        ? null
        : Number(period.cost) / Number(period.budget);

    const recurringTiles: Tile[] = [
      {
        key: 'revenue',
        label: t('finance.kpi.revenue'),
        value: m(model.revenue),
        figure: model.revenue,
        formula: t('finance.kpi.revenueFormula'),
        sub:
          model.revenueBillable > 0.4
            ? t('finance.kpi.ofWhichBillable', { amount: m(model.revenueBillable) })
            : '',
        tip: {
          name: t('finance.tip.revenue.name'),
          what: t('finance.tip.revenue.what'),
          calc: t('finance.tip.revenue.calc'),
          note: t('finance.tip.revenue.note'),
        },
      },
      {
        key: 'budget',
        label: t('finance.kpi.budget'),
        value: m(model.budget),
        figure: model.budget,
        sub: t('finance.kpi.bac'),
        tip: {
          name: t('finance.tip.bac.name'),
          what: t('finance.tip.bac.what'),
          calc: t('finance.tip.bac.calc'),
          note: t('finance.tip.bac.note'),
        },
      },
      {
        key: 'periodSpend',
        label: t('finance.kpi.periodSpend'),
        value: percent(periodShare),
        figure: periodShare,
        valueClass: bad(period !== null && Number(period.cost) > Number(period.budget) + 0.4),
        formula: period === null ? '–' : `${m(Number(period.cost))} / ${m(Number(period.budget))}`,
        sub: period === null ? '' : `#${String(period.index)}`,
        tip: {
          name: t('finance.tip.periodSpend.name'),
          what: t('finance.tip.periodSpend.what'),
          calc: t('finance.tip.periodSpend.calc'),
          note: t('finance.tip.periodSpend.note'),
        },
        bar: {
          pct: barWidth(periodShare === null ? 0 : periodShare * 100, 100),
          color:
            period !== null && Number(period.cost) > Number(period.budget) + 0.4
              ? 'var(--destructive)'
              : 'var(--primary)',
        },
        timeBar: barWidth((model.periodWorkdaysDone / model.periodWorkdays) * 100, 100),
      },
      {
        key: 'runRate',
        label: t('finance.kpi.runRate'),
        value: t('finance.kpi.perDay', { amount: m(model.runRate) }),
        figure: model.runRate,
        valueClass: bad(model.runRate > model.allowedRunRate + 0.4),
        formula:
          period === null
            ? '–'
            : t('finance.kpi.runRateFormula', {
                amount: m(Number(period.cost)),
                days: model.periodWorkdaysDone,
              }),
        sub: t('finance.kpi.allowedPerDay', { amount: m(model.allowedRunRate) }),
        tip: {
          name: t('finance.tip.runRate.name'),
          what: t('finance.tip.runRate.what'),
          calc: t('finance.tip.runRate.calc'),
          note: t('finance.tip.runRate.note'),
        },
      },
      {
        key: 'overrun',
        label: t('finance.kpi.overrun'),
        value: s(model.periodOverrun),
        figure: model.periodOverrun,
        valueClass: bad(model.periodOverrun > 0.4),
        formula: t('finance.kpi.overrunFormula'),
        sub: t('finance.kpi.projected', { amount: m(model.projectedPeriodCost) }),
        tip: {
          name: t('finance.tip.overrun.name'),
          what: t('finance.tip.overrun.what'),
          calc: t('finance.tip.overrun.calc'),
          note: t('finance.tip.overrun.note'),
        },
      },
      {
        key: 'periodMargin',
        label:
          marginMode === 'plan'
            ? t('finance.kpi.periodPlanMargin')
            : t('finance.kpi.periodForecastMargin'),
        value: s(marginMode === 'plan' ? model.periodPlannedMargin : model.periodForecastMargin),
        figure: marginMode === 'plan' ? model.periodPlannedMargin : model.periodForecastMargin,
        valueClass: bad(
          (marginMode === 'plan' ? model.periodPlannedMargin : model.periodForecastMargin) < -0.4,
        ),
        menu: marginMenu,
        tip:
          marginMode === 'plan'
            ? {
                name: t('finance.tip.periodPlanMargin.name'),
                what: t('finance.tip.periodPlanMargin.what'),
                calc: t('finance.tip.periodPlanMargin.calc'),
                note: t('finance.tip.marginSwitch'),
              }
            : {
                name: t('finance.tip.periodForecastMargin.name'),
                what: t('finance.tip.periodForecastMargin.what'),
                calc: t('finance.tip.periodForecastMargin.calc'),
                note: t('finance.tip.marginSwitch'),
              },
      },
      {
        key: 'cpi',
        label: t('finance.kpi.cpi'),
        value: index(model.cpi),
        figure: model.cpi,
        valueClass: bad(model.cpi !== null && model.cpi < 0.92),
        formula: t('finance.tip.cpi.calc'),
        sub: t('finance.kpi.cpiSub'),
        tip: {
          name: t('finance.tip.cpi.name'),
          what: t('finance.tip.cpi.whatRecurring'),
          calc: t('finance.tip.cpi.calc'),
          note: t('finance.tip.cpi.note'),
        },
      },
      {
        key: 'layers',
        label: t('finance.kpi.layers'),
        /**
         * ┌────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ ONE TOTAL, NOT TWO FIGURES — round 5 item 21.                       │
         * └────────────────────────────────────────────────────────┘
         *
         * *"Keretes | számlázható kártyán egy összeg legyen."* I argued the other way here — that the
         * two layers are the contract's central trade and a sum hides it — and the argument was
         * about the wrong surface. `a | b` in a 20px figure is not two numbers offered for
         * comparison; it is one card that has to be parsed before it can be read, beside seven
         * that each carry a single figure.
         *
         * ⚠️ **The split is not lost — it lives where it can actually be compared.** The tip under
         * the label names both layers, and the profitability table below draws them per period with
         * a column each, which is the surface that comparison wanted in the first place.
         */
        value: m(
          model.revenueRetainer -
            (model.actualCostRetainer + model.externalCost) +
            (model.revenueBillable - model.actualCostBillable),
        ),
        figure:
          model.revenueRetainer -
          (model.actualCostRetainer + model.externalCost) +
          (model.revenueBillable - model.actualCostBillable),
        formula: t('finance.kpi.layersFormula'),
        sub: t('finance.kpi.layersSub'),
        tip: {
          name: t('finance.tip.layers.name'),
          what: t('finance.tip.layers.what'),
          calc: t('finance.tip.layers.calc'),
          note: t('finance.tip.layers.note'),
        },
      },
    ];

    const fixedTiles: Tile[] = [
      {
        key: 'revenue',
        label: t('finance.kpi.revenueFixed'),
        value: m(model.revenue),
        figure: model.revenue,
        formula: t('finance.kpi.revenueFixedFormula'),
        sub: t('finance.kpi.accrual'),
        tip: {
          name: t('finance.tip.revenueFixed.name'),
          what: t('finance.tip.revenueFixed.what'),
          calc: t('finance.tip.revenueFixed.calc'),
          note: t('finance.tip.revenueFixed.note'),
        },
      },
      {
        key: 'budget',
        label: t('finance.kpi.budgetFixed'),
        value: m(model.budget),
        figure: model.budget,
        sub: t('finance.kpi.bac'),
        tip: {
          name: t('finance.tip.bac.name'),
          what: t('finance.tip.bacFixed.what'),
          calc: t('finance.tip.bacFixed.calc'),
          note: t('finance.tip.bacFixed.note'),
        },
      },
      {
        key: 'planned',
        label: t('finance.kpi.plannedCost'),
        value: m(model.plannedCost),
        figure: model.plannedCost,
        formula: view.plannedFromFrame
          ? t('finance.kpi.fromFrame')
          : t('finance.kpi.plannedFormula'),
        sub: view.plannedFromFrame ? t('finance.kpi.thinPlan') : undefined,
        tip: {
          name: t('finance.tip.plannedCost.name'),
          what: t('finance.tip.plannedCost.what'),
          calc: t('finance.tip.plannedCost.calc'),
          note: t('finance.tip.plannedCost.note'),
        },
        bar: {
          pct: barWidth(model.plannedCost, model.budget),
          color:
            model.plannedCost > model.budget + 0.4
              ? 'var(--destructive)'
              : 'color-mix(in oklch, var(--primary) 45%, transparent)',
        },
      },
      {
        key: 'actual',
        label: t('finance.kpi.actualCost'),
        value: m(model.actualCost),
        figure: model.actualCost,
        valueClass: bad(model.actualCost > model.budget + 0.4 && model.budget > 0.4),
        formula: t('finance.kpi.actualFormula'),
        sub:
          model.externalCost > 0.4
            ? t('finance.kpi.ofWhichExternal', { amount: m(model.externalCost) })
            : undefined,
        tip: {
          name: t('finance.tip.actualCost.name'),
          what: t('finance.tip.actualCost.what'),
          calc: t('finance.tip.actualCost.calc'),
          note: t('finance.tip.actualCost.note'),
        },
        bar: {
          pct: barWidth(model.actualCost, model.budget),
          color: model.actualCost > model.budget + 0.4 ? 'var(--destructive)' : 'var(--primary)',
        },
      },
      {
        key: 'cpi',
        label: t('finance.kpi.cpi'),
        value: index(model.cpi),
        figure: model.cpi,
        valueClass: bad(model.cpi !== null && model.cpi < 0.92),
        tip: {
          name: t('finance.tip.cpi.name'),
          what: t('finance.tip.cpi.what'),
          calc: t('finance.tip.cpi.calc'),
          note: t('finance.tip.cpi.note'),
        },
      },
      {
        key: 'spi',
        label: t('finance.kpi.spi'),
        value: index(model.spi),
        figure: model.spi,
        valueClass: bad(model.spi !== null && model.spi < 0.92),
        tip: {
          name: t('finance.tip.spi.name'),
          what: t('finance.tip.spi.what'),
          calc: view.plannedValueFromFrame
            ? t('finance.tip.spi.calcThin')
            : t('finance.tip.spi.calc'),
          note: t('finance.tip.spi.note'),
        },
      },
      {
        key: 'eac',
        label: t('eac.row', { mode: eacLabel(t, eacMode) }),
        value: m(model.eac),
        figure: model.eac,
        valueClass: bad(model.eac !== null && model.eac > model.budget + 0.4 && model.budget > 0.4),
        menu: eacMenu,
        tip: {
          name: t('finance.tip.eac.name'),
          what: t('finance.tip.eac.what'),
          calc: `${eacFormula(eacMode)} — ${eacHint(t, eacMode)}`,
          note: t('finance.tip.eac.note', { amount: s(model.vac) }),
        },
      },
      {
        key: 'margin',
        label:
          marginMode === 'plan' ? t('finance.kpi.planMargin') : t('finance.kpi.forecastMargin'),
        value: s(marginMode === 'plan' ? model.plannedMargin : model.forecastMargin),
        figure: marginMode === 'plan' ? model.plannedMargin : model.forecastMargin,
        valueClass: bad(
          (marginMode === 'plan' ? model.plannedMargin : model.forecastMargin) < -0.4,
        ),
        menu: marginMenu,
        tip:
          marginMode === 'plan'
            ? {
                name: t('finance.tip.planMargin.name'),
                what: t('finance.tip.planMargin.what'),
                calc: t('finance.tip.planMargin.calc'),
                note: t('finance.tip.marginSwitch'),
              }
            : {
                name: t('finance.tip.forecastMargin.name'),
                what: t('finance.tip.forecastMargin.what'),
                calc: t('finance.tip.forecastMargin.calc'),
                note: t('finance.tip.marginSwitch'),
              },
      },
    ];

    const tiles = (view.recurring ? recurringTiles : fixedTiles).filter(
      (tile) =>
        (!internal || !INCOME_TILES.has(tile.key)) && (!noBudget || !BUDGET_TILES.has(tile.key)),
    );
    return tiles;
  };
  const tiles = tilesOf(view, model);
  const todays = live === null ? null : tilesOf(live.view, live.model);

  return (
    <div
      className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${internal ? 'xl:grid-cols-3' : 'xl:grid-cols-4'}`}
    >
      {tiles.map((tile) => (
        <div key={tile.key} className="bg-card rounded-xl border p-3 shadow-sm">
          <div className="text-muted-foreground flex items-center gap-1 text-[11px] font-medium tracking-wide uppercase">
            {tile.menu === undefined ? (
              <FinanceTip tip={tile.tip} calcLabel={t('finance.calc')}>
                {tile.label}
              </FinanceTip>
            ) : (
              <DropdownMenu
                width={300}
                align="start"
                label={tile.label}
                /* ⚠️ Handed over as they are. They used to be copied field by field, and the
                   greyed EACs' tooltips went missing on this tab because the copy left that field
                   out — the effort tab, which passes them straight, showed them. */
                items={[...tile.menu]}
              >
                {/* ⚠️ The LABEL is the control — the mockup puts the choice on the word rather than
                    adding a second target beside a figure that is already the point of the tile. */}
                <button
                  type="button"
                  className="hover:text-foreground flex items-center gap-1 uppercase transition-colors"
                >
                  {tile.label}
                  <Icon name="chevron-down" size={11} className="opacity-60" />
                </button>
              </DropdownMenu>
            )}
          </div>

          {/* ⚠️ `markup.html` 2173 — `mt-2.5 text-[20px] leading-none font-medium`. The figure IS
              the tile; unweighted it sits at the same visual level as the caption above it. */}
          <div
            className={`mt-2.5 text-[20px] leading-none font-medium tabular-nums ${tile.valueClass ?? ''}`}
          >
            {tile.value}
          </div>
          <KpiDelta now={todays?.find((one) => one.key === tile.key)} then={tile.figure} />

          {/**
           * ┌─────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ A TITLE AND A NUMBER — round 5 item 20.                        │
           * └─────────────────────────────────────────────────────┘
           *
           * The same cut item 11 made on the project dashboard, on the screen it was copied from:
           * the formula line, the sub-caption and both bars are gone, and eight cards that each
           * carried four lines now carry two.
           *
           * ⚠️⚠️ **NOTHING IS LOST, BECAUSE THE TIP ALWAYS HELD IT.** Every tile's label opens a
           * `FinanceTip` with the metric's name, what it means, *its calculation* and a note — so
           * the formula under the figure was the tip's third line printed twice, and the bar was
           * a share the tip states in words. What the card now does is say which figure this is
           * and what it reads; the explanation is one hover away, where somebody who wants it
           * goes looking.
           *
           * ⚠️ **`formula`, `sub`, `bar` and `timeBar` are still BUILT.** They are derived in
           * `finance-model.ts` from figures the panel computes anyway, they carry the arguments
           * that justify them, and a tile is one prop from drawing them again. What changed is
           * what the card DRAWS — the same shape `effort-panels.tsx` kept for item 11.
           */}
        </div>
      ))}
    </div>
  );
}

/* ── the budget bars ───────────────────────────────────────────────────────── */

export function BudgetPanel({
  view,
  model,
  currency,
  eacMode,
  includeExternal,
  onEacMode,
  onIncludeExternal,
}: {
  readonly view: ContractFinance;
  readonly model: FinanceModel;
  readonly currency: Currency;
  readonly eacMode: EacMode;
  readonly includeExternal: boolean;
  readonly onEacMode: (mode: EacMode) => void;
  readonly onIncludeExternal: (included: boolean) => void;
}) {
  const t = useT();
  const m = (value: number | null): string => money(value, currency);
  const s = (value: number | null): string => signedMoney(value, currency);

  /* ⚠️ Every bar is drawn against the SAME scale, a little wider than the largest figure — so an
     EAC that overruns visibly overhangs the budget mark rather than being clipped to it. */
  const scale =
    Math.max(model.budget, model.plannedCost, model.actualCost, model.eac ?? 0, 1) * 1.06;
  const share = (value: number): string =>
    model.budget > 0.4 ? `${String(Math.round((value / model.budget) * 100))}%` : '–';

  const rows: {
    key: string;
    label: string;
    value: number;
    color: string;
    tip: FinanceTipText;
    className?: string;
    /**
     * ┌─────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE CHOICE SITS ON THE ROW IT CHANGES — round 5 item 22.        │
     * └─────────────────────────────────────────────────────────┘
     *
     * *"Külső költség és a Trendtartó/Tervtartó EAC a sor címkéjéről nyissa a lenyilót."* Both
     * controls used to sit in the panel's bottom-right corner, below the last bar: two words that
     * named a mode without saying which of six rows they governed, and a reader had to change one
     * and watch which bar moved to find out.
     *
     * ⚠️ **The label was already the mode's name**, on both rows — *Külső költség (belül)* and
     * *Trendtartó EAC* — so the row was announcing a choice it could not offer. The same shape
     * the KPI tiles use for their margin and EAC menus.
     *
     * ⚠️ **A row with a menu has no `FinanceTip`.** One label cannot be both a hover target and
     * a click target; the tile grid resolved this the same way, and the tip's text moves into the
     * menu's own per-option descriptions, which is where somebody choosing needs it.
     */
    menu?: {
      label: string;
      items: readonly {
        label: string;
        description: string;
        checked: boolean;
        disabled?: boolean;
        tooltip?: string;
        onSelect: () => void;
      }[];
    };
  }[] = [
    {
      key: 'budget',
      label: t('finance.bar.budget'),
      value: model.budget,
      color: 'color-mix(in oklch, var(--foreground) 18%, transparent)',
      tip: {
        name: t('finance.tip.bac.name'),
        what: t('finance.tip.bac.what'),
        calc: view.recurring ? t('finance.tip.bac.calc') : t('finance.tip.bacFixed.calc'),
        note: t('finance.tip.bar.budgetNote'),
      },
    },
    {
      key: 'planned',
      label: t('finance.bar.planned'),
      value: model.plannedCost,
      color: 'color-mix(in oklch, var(--primary) 30%, transparent)',
      tip: {
        name: t('finance.tip.plannedCost.name'),
        what: t('finance.tip.plannedCost.what'),
        calc: view.plannedFromFrame
          ? t('finance.tip.plannedCost.calcThin')
          : t('finance.tip.plannedCost.calc'),
        note: t('finance.tip.bar.plannedNote'),
      },
    },
    {
      key: 'actual',
      label: t('finance.bar.actual'),
      value: model.actualCost,
      color: model.actualCost > model.budget + 0.4 ? 'var(--destructive)' : 'var(--primary)',
      className: bad(model.actualCost > model.budget + 0.4),
      tip: {
        name: t('finance.tip.actualCost.name'),
        what: t('finance.tip.actualCost.what'),
        calc: t('finance.tip.actualCost.calc'),
        note: t('finance.tip.bar.actualNote'),
      },
    },
    /**
     * ⚠️ **THE PROJECT'S OWN COSTS — a row only while the server reports them apart.** Since
     * 4 Nov 2026 (the product owner) they are inside *Külső költség* on every contract, and
     * `projectExternalCost` is zero, so this row is not drawn; it stays for a read that reports
     * them beside the cost again. A permanent zero would be furniture.
     */
    ...(model.projectExternalCost > 0.4
      ? [
          {
            key: 'projectExternal',
            label: t('finance.bar.projectExternal'),
            value: model.projectExternalCost,
            color: 'color-mix(in oklch, var(--foreground) 25%, transparent)',
            tip: {
              name: t('finance.tip.projectExternal.name'),
              what: t('finance.tip.projectExternal.what'),
              calc: t('finance.tip.projectExternal.calc'),
              note: t('finance.tip.projectExternal.note'),
            },
          },
        ]
      : []),
    {
      key: 'external',
      label: includeExternal ? t('finance.bar.external') : t('finance.bar.externalExcluded'),
      value: model.externalCost,
      color: 'color-mix(in oklch, var(--foreground) 45%, transparent)',
      tip: {
        name: t('finance.tip.external.name'),
        what: t('finance.tip.external.what'),
        calc: t('finance.tip.external.calc'),
        note: includeExternal
          ? t('finance.tip.external.noteIn')
          : t('finance.tip.external.noteOut'),
      },
      menu: {
        label: t('finance.external.label'),
        items: [
          {
            label: t('finance.external.in'),
            description: t('finance.external.inHint'),
            checked: includeExternal,
            onSelect: () => {
              onIncludeExternal(true);
            },
          },
          {
            label: t('finance.external.out'),
            description: t('finance.external.outHint'),
            checked: !includeExternal,
            onSelect: () => {
              onIncludeExternal(false);
            },
          },
        ],
      },
    },
    {
      key: 'earned',
      label: t('finance.bar.earned'),
      value: model.earnedValue,
      color: 'color-mix(in oklch, var(--primary) 60%, transparent)',
      tip: {
        name: t('finance.tip.ev.name'),
        what: view.recurring ? t('finance.tip.ev.whatRecurring') : t('finance.tip.ev.what'),
        calc: view.recurring ? t('finance.tip.ev.calcRecurring') : t('finance.tip.ev.calc'),
        note: t('finance.tip.ev.note'),
      },
    },
    {
      key: 'eac',
      label: t('eac.row', { mode: eacLabel(t, eacMode) }),
      value: model.eac ?? 0,
      color:
        (model.eac ?? 0) > model.budget + 0.4
          ? 'color-mix(in oklch, var(--destructive) 45%, transparent)'
          : 'color-mix(in oklch, var(--primary) 22%, transparent)',
      className: bad((model.eac ?? 0) > model.budget + 0.4),
      tip: {
        name: t('finance.tip.eac.name'),
        what: t('finance.tip.eac.what'),
        calc: `${eacFormula(eacMode)} — ${eacHint(t, eacMode)}`,
        note: t('finance.tip.bar.eacNote'),
      },
      menu: {
        label: t('finance.eac.label'),
        items: eacMenuItems(t, view.eac, eacMode, onEacMode),
      },
    },
  ];

  const footnotes = [
    {
      key: 'etc',
      label: t('finance.foot.etc'),
      value: m(model.etc),
      className: '',
      tip: {
        name: t('finance.tip.etc.name'),
        what: t('finance.tip.etc.what'),
        calc: t('finance.tip.etc.calc'),
        note: t('finance.tip.etc.note'),
      },
    },
    {
      key: 'vac',
      label: t('finance.foot.vac'),
      value: s(model.vac),
      className: bad(model.vac !== null && model.vac < -0.4),
      tip: {
        name: t('finance.tip.vac.name'),
        what: t('finance.tip.vac.what'),
        calc: t('finance.tip.vac.calc'),
        note: t('finance.tip.vac.note'),
      },
    },
    {
      key: 'cv',
      label: t('finance.foot.cv'),
      value: s(model.costVariance),
      className: bad(model.costVariance < -0.4),
      tip: {
        name: t('finance.tip.cv.name'),
        what: t('finance.tip.cv.what'),
        calc: t('finance.tip.cv.calc'),
        note: t('finance.tip.cv.note'),
      },
    },
    {
      key: 'sv',
      label: t('finance.foot.sv'),
      value: s(model.scheduleVariance),
      className: bad(model.scheduleVariance < -0.4),
      tip: {
        name: t('finance.tip.sv.name'),
        what: t('finance.tip.sv.what'),
        calc: t('finance.tip.sv.calc'),
        note: t('finance.tip.sv.note'),
      },
    },
    {
      key: 'tcpi',
      label: t('finance.foot.tcpi'),
      value: index(model.tcpi),
      className: bad(model.tcpi !== null && model.tcpi > 1.08),
      tip: {
        name: t('finance.tip.tcpi.name'),
        what: t('finance.tip.tcpi.what'),
        calc: t('finance.tip.tcpi.calc'),
        note: t('finance.tip.tcpi.note'),
      },
    },
    {
      key: 'pv',
      label: t('finance.foot.pv'),
      value: m(model.plannedValue),
      className: '',
      tip: {
        name: t('finance.tip.pv.name'),
        what: t('finance.tip.pv.what'),
        calc: view.plannedValueFromFrame ? t('finance.tip.pv.calcThin') : t('finance.tip.pv.calc'),
        note: t('finance.tip.pv.note'),
      },
    },
  ];

  return (
    <div className="bg-card rounded-xl border p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          <FinanceTip
            calcLabel={t('finance.calc')}
            tip={{
              name: t('finance.tip.utilisation.name'),
              what: t('finance.tip.utilisation.what'),
              calc: t('finance.tip.utilisation.calc'),
              note: t('finance.tip.utilisation.note'),
            }}
          >
            {t('finance.panel.utilisation')}
          </FinanceTip>
        </div>
        <div className="flex-1" />
        <span className="text-muted-foreground text-[11px] tabular-nums">
          {t('finance.panel.budgetIs', { amount: m(model.budget) })}
        </span>
      </div>

      {/**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ MEASURED OUT OF THE MOCKUP'S OWN MARKUP, NOT EYEBALLED.              │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * `markup.html` 2196–2218: a 176px uppercase label at `text-[11px] font-medium
       * tracking-wide`, a 14px track capped at 300px, the value `text-[12.5px] font-medium` in a
       * 132px column and the share muted at 11.5px in 42px — with `gap-5` between rows. Ours was
       * a plain sentence-case label, a 12px track, unweighted figures and `gap-2`, which is why
       * the panel read as a list of numbers where the mockup reads as a chart.
       *
       * ⚠️ **The weights are the mockup's and they are load-bearing.** A row here is a LABEL and a
       * FIGURE; drawn at one weight the eye has nothing to land on, which is the difference the
       * tester saw before they could name it.
       */}
      <div className="relative mt-4 flex flex-col gap-5">
        {rows.map((row) => (
          <div
            key={row.key}
            className="grid grid-cols-[minmax(0,176px)_minmax(0,1fr)_132px_42px] items-center gap-2 phone:grid-cols-[88px_minmax(48px,1fr)_auto_36px]"
          >
            <span
              className={`text-muted-foreground truncate text-[11px] font-medium tracking-wide uppercase ${row.className ?? ''}`}
            >
              {row.menu === undefined ? (
                <FinanceTip tip={row.tip} calcLabel={t('finance.calc')}>
                  {row.label}
                </FinanceTip>
              ) : (
                <DropdownMenu
                  width={320}
                  align="start"
                  label={row.menu.label}
                  items={[...row.menu.items]}
                >
                  <button
                    type="button"
                    className="hover:text-foreground flex max-w-full items-center gap-1 uppercase transition-colors"
                  >
                    <span className="truncate">{row.label}</span>
                    <Icon name="chevron-down" size={11} className="shrink-0 opacity-60" />
                  </button>
                </DropdownMenu>
              )}
            </span>
            {/**
             * ⚠️⚠️ **NO WIDTH CAP ON THE TRACK.** The mockup's own bullet row is
             * `h-[14px] min-w-0 flex-1 rounded-sm` — the track takes whatever the row has left.
             * Pinned to 300px inside a panel the width of the screen, a bar at 38% of its scale
             * drew at a ninth of the row: seven lengths that all looked like rounding errors, with
             * the büdzsé mark stranded in the middle of empty space. The percentages beside them
             * were right the whole time; the ruler was not.
             *
             * ⚠️ **The hours dashboard inherited this from here** and was reported first. Fixing
             * the copy and leaving the original is how one panel ends up looking like two.
             */}
            <span className="bg-muted/40 relative h-[14px] min-w-0 rounded-sm">
              <span
                className="absolute inset-y-0 left-0 rounded-sm"
                style={{ width: `${String(barWidth(row.value, scale))}%`, background: row.color }}
              />
              {/**
               * ⚠️⚠️ **THE BUDGET MARK IS PER BAR AND IT OVERHANGS.** It used to be one absolutely
               * positioned line over the whole block, placed by a `calc()` that had to know the
               * label column's width — so it drifted the moment anything beside it changed, and at
               * 1px inside the track it was invisible against a bar of the same length. The mockup
               * draws it inside each track at `inset-y-[-3px] w-[2px]`: two pixels, sticking out
               * top and bottom, over every bar. That mark is what makes six lengths readable.
               */}
              <span
                aria-hidden
                className="bg-foreground/70 absolute inset-y-[-3px] w-[2px]"
                style={{ left: `${String(barWidth(model.budget, scale))}%` }}
              />
            </span>
            <span
              className={`text-right text-[12.5px] font-medium tabular-nums ${row.className ?? ''}`}
            >
              {m(row.value)}
            </span>
            <span className="text-muted-foreground text-right text-[11.5px] tabular-nums">
              {share(row.value)}
            </span>
          </div>
        ))}
      </div>

      {/* ⚠️ 2 Nov 2026 · no line of EAC notes under the bars, here or on the effort tab: the mode
          is named on its row and the range is the band on the chart, so it said both twice. */}

      {/* ⚠️⚠️ **THE TWO CORNER DROPDOWNS ARE GONE — they are on their rows now (item 22).** The
          note that used to sit here claimed the switch *"lives ON the row it changes"*, which was
          the right principle attached to a control in the opposite corner of the panel. */}

      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 border-t pt-3 sm:grid-cols-2">
        {/* ⚠️ `markup.html` 2220–2228: a dashed rule under each pair and a weighted figure. The
            label is the question and the figure is the answer — one weight for both makes a reader
            parse the row twice. */}
        {footnotes.map((note) => (
          <span
            key={note.key}
            className="flex items-center gap-1.5 border-b border-dashed py-1 text-[11.5px] last:border-0"
          >
            <span className="text-muted-foreground min-w-0 truncate">
              <FinanceTip tip={note.tip} calcLabel={t('finance.calc')}>
                {note.label}
              </FinanceTip>
            </span>
            <span className="flex-1" />
            <span className={`shrink-0 text-[12.5px] font-medium tabular-nums ${note.className}`}>
              {note.value}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── the profitability list ────────────────────────────────────────────────── */

export function ProfitabilityPanel({
  view,
  model,
  currency,
}: {
  readonly view: ContractFinance;
  readonly model: FinanceModel;
  readonly currency: Currency;
}) {
  const t = useT();
  const m = (value: number | null): string => money(value, currency);
  const s = (value: number | null): string => signedMoney(value, currency);

  const rows = [
    {
      key: 'revenue',
      label: t('finance.list.revenue'),
      value: m(model.revenue),
      className: '',
      tip: {
        name: t('finance.tip.revenue.name'),
        what: t('finance.tip.list.revenue'),
        calc: view.recurring ? t('finance.tip.revenue.calc') : t('finance.kpi.revenueFixedFormula'),
        note: t('finance.tip.accrual'),
      },
    },
    {
      key: 'planMargin',
      label: t('finance.list.planMargin'),
      value: s(model.plannedMargin),
      className: bad(model.plannedMargin < -0.4),
      tip: {
        name: t('finance.tip.planMargin.name'),
        what: t('finance.tip.planMargin.what'),
        calc: t('finance.tip.planMargin.calc'),
        note: t('finance.tip.planMargin.note'),
      },
    },
    {
      key: 'planMarginPct',
      label: t('finance.list.planMarginPct'),
      value: percent(model.plannedMarginPct),
      className: bad(model.plannedMarginPct !== null && model.plannedMarginPct < 0),
      tip: {
        name: t('finance.tip.planMarginPct.name'),
        what: t('finance.tip.planMarginPct.what'),
        calc: t('finance.tip.planMarginPct.calc'),
        note: t('finance.tip.planMarginPct.note'),
      },
    },
    {
      key: 'forecastRevenue',
      label: t('finance.list.forecastRevenue'),
      value: m(model.forecastRevenue),
      className: '',
      tip: {
        name: t('finance.tip.forecastRevenue.name'),
        what: t('finance.tip.forecastRevenue.what'),
        calc: t('finance.tip.forecastRevenue.calc'),
        note: t('finance.tip.forecastRevenue.note'),
      },
    },
    {
      key: 'forecastMargin',
      label: t('finance.list.forecastMargin'),
      value: s(model.forecastMargin),
      className: bad(model.forecastMargin < -0.4),
      tip: {
        name: t('finance.tip.forecastMargin.name'),
        what: t('finance.tip.forecastMargin.what'),
        calc: t('finance.tip.forecastMargin.calc'),
        note: t('finance.tip.forecastMargin.note'),
      },
    },
    {
      key: 'forecastMarginPct',
      label: t('finance.list.forecastMarginPct'),
      value: percent(model.forecastMarginPct),
      className: bad(model.forecastMarginPct !== null && model.forecastMarginPct < 0),
      tip: {
        name: t('finance.tip.forecastMarginPct.name'),
        what: t('finance.tip.forecastMarginPct.what'),
        calc: t('finance.tip.forecastMarginPct.calc'),
        note: t('finance.tip.forecastMarginPct.note'),
      },
    },
    {
      key: 'actualRate',
      label: t('finance.list.actualRate'),
      value: m(model.actualHourlyRate),
      className: '',
      tip: {
        name: t('finance.tip.actualRate.name'),
        what: t('finance.tip.actualRate.what'),
        calc: t('finance.tip.actualRate.calc'),
        note: t('finance.tip.actualRate.note'),
      },
    },
    {
      key: 'targetRate',
      label: t('finance.list.targetRate'),
      value: m(model.targetHourlyRate),
      className: '',
      tip: {
        name: t('finance.tip.targetRate.name'),
        what: t('finance.tip.targetRate.what'),
        calc: t('finance.tip.targetRate.calc'),
        note: t('finance.tip.targetRate.note'),
      },
    },
    /* FI-1 · the third rate: the same revenue over the ALLOCATED hours rather than the logged. */
    {
      key: 'plannedRate',
      label: t('finance.list.plannedRate'),
      value: m(model.plannedHourlyRate),
      className: '',
      tip: {
        name: t('finance.tip.plannedRate.name'),
        what: t('finance.tip.plannedRate.what'),
        calc: t('finance.tip.plannedRate.calc'),
        note: t('finance.tip.plannedRate.note'),
      },
    },
    {
      key: 'invoiced',
      label: t('finance.list.invoiced'),
      value: m(model.invoiced),
      className: '',
      tip: {
        name: t('finance.tip.invoiced.name'),
        what: t('finance.tip.invoiced.what'),
        calc: t('finance.tip.invoiced.calc'),
        note: t('finance.tip.invoiced.note'),
      },
    },
    {
      key: 'invoicedPct',
      label: t('finance.list.invoicedPct'),
      value: percent(model.invoicedPct),
      className: '',
      tip: {
        name: t('finance.tip.invoicedPct.name'),
        what: t('finance.tip.invoicedPct.what'),
        calc: t('finance.tip.invoicedPct.calc'),
        note: t('finance.tip.invoicedPct.note'),
      },
    },
    {
      key: 'wip',
      label: t('finance.list.wip'),
      value: m(model.workInProgress),
      className: bad(model.workInProgress < -0.4),
      tip: {
        name: t('finance.tip.wip.name'),
        what: t('finance.tip.wip.what'),
        calc: t('finance.tip.wip.calc'),
        note: t('finance.tip.wip.note'),
      },
    },
  ];

  return (
    <div className="bg-card rounded-xl border p-4 shadow-sm">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        <FinanceTip
          calcLabel={t('finance.calc')}
          tip={{
            name: t('finance.tip.profitability.name'),
            what: t('finance.tip.profitability.what'),
            note: t('finance.tip.profitability.note'),
          }}
        >
          {t('finance.panel.profitability')}
        </FinanceTip>
      </div>
      {/* ⚠️ `markup.html` 2237–2244: the label is an uppercase 10.5px `font-medium` caption and the
          figure is a weighted 12.5px — eleven rows at one weight is a wall of text, which is what
          this panel had become. */}
      <div className="mt-1.5 flex flex-col">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-1.5 border-b py-[6px] last:border-0">
            <span className="text-muted-foreground min-w-0 truncate text-[10.5px] font-medium tracking-wide uppercase">
              <FinanceTip tip={row.tip} calcLabel={t('finance.calc')}>
                {row.label}
              </FinanceTip>
            </span>
            <div className="flex-1" />
            <span className={`shrink-0 text-[12.5px] font-medium tabular-nums ${row.className}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
